import React, { useState, useEffect } from "react";
import MainLayout from "../layouts/MainLayout";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import Pagination from "../components/Pagination";
import { useLoading } from "../context/LoadingContext";
import { Modal } from "bootstrap";
import useApi from "../utils/useApi";
import Toaster, { ToastContainerComponent } from "../components/Toaster"; // ✅ Toaster for notifications
import { useAuth } from "../context/AuthContext";
import Breadcrumbs from "../components/Breadcrumbs";
import { generateFileTokenApi } from "../utils/api";

const FileSettings = () => {
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const [defaultFiles, setDefaultFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const filesPerPage = parseInt(process.env.REACT_APP_PER_PAGE) || 50; // Default: 5 items per page
  //const [totalFoldersData, settotalFoldersData] = useState(0);
  const [folderDetails, setFolderDetails] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);

  const [name, setName] = useState("");
  const navigate = useNavigate();
  const { "*": dynamicPath } = useParams();
  const pathSegments = dynamicPath ? dynamicPath.split("/") : [];
  const entity_id = pathSegments[pathSegments.length - 1]; // Last segment is the folder ID
  const { user } = useAuth();

  const fetchFoldersData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        length: filesPerPage,
        sort: "desc",
        entity_id: entity_id,
      };
      const response = await callApi({
        endpoint: "/admin/default-file",
        params,
      });
      console.log(
        "response from fileSetting default file list",
        response?.data
      );

      if (response?.success) {
        setDefaultFiles(response?.data || []);
        // settotalFoldersData(response.data.total || 0);
      } else {
        Toaster.error("Failed to load folder details."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error fetching folder details:", error);
      Toaster.error("Error fetching folder details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item?.status === "active" ? "inactive" : "active";

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/default-file/update-status", // replace with your actual endpoint
        method: "PUT",
        data: {
          file_id: item._id,
          status: newStatus,
        },
      });

      if (response?.success) {
        Toaster.success("Status updated successfully!");
        fetchFoldersData(); // Refresh the table
      } else {
        Toaster.error(response?.data?.message || "Failed to update status.");
      }
    } catch (error) {
      console.error("Status update failed:", error);
      Toaster.error("Error updating status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFoldersData();
  }, [entity_id, currentPage, dynamicPath]);

  const detailsFolder = async (location) => {
    if (location.startsWith("../")) {
      location = location.replace(/^(\.\.\/)+/, "");
    }

    try {
      setLoading(true);

      // Generate secure file token - same pattern as FolderDetails
      const tokenResponse = await generateFileTokenApi(location, false);

      if (tokenResponse?.data?.success) {
        const fileToken = tokenResponse.data.data.token;

        let fileUrl = `${process.env.REACT_APP_BACKEND_FILE_URL_ROOT}${location}`;
        fileUrl = fileUrl.replace(/["]/g, "");

        // Pass both JWT token and fileToken in URL (same as FolderDetails)
        const jwtToken = localStorage.getItem("accessToken");
        if (jwtToken) {
          fileUrl += `?token=${encodeURIComponent(jwtToken)}&fileToken=${encodeURIComponent(fileToken)}`;
        } else {
          fileUrl += `?fileToken=${encodeURIComponent(fileToken)}`;
        }

        setPreviewModal(true);
        setPreviewFileUrl(fileUrl);
      } else {
        Toaster.error("Failed to generate secure file access");
      }
    } catch (error) {
      console.error("Error generating file token:", error);
      Toaster.error("Failed to access file");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async () => {
    if (!previewFileUrl) return;

    try {
      setLoading(true);

      // Add download=1 query parameter to force download
      const downloadUrl = previewFileUrl + (previewFileUrl.includes('?') ? '&' : '?') + 'download=1';

      // Use a temporary <a> element — no memory/DOM leak, no 5s timer
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Toaster.success("File download started!");
    } catch (error) {
      console.error("Error downloading file:", error);
      Toaster.error("Failed to download file. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  // Direct download from table row - same pattern as FolderDetails
  const downloadFileDirectly = async (location) => {
    if (!location) return;

    try {
      setLoading(true);

      let cleanedLocation = location.replace(/^\.\.\//, "");
      cleanedLocation = cleanedLocation.replace(/\/+$/, "");

      // Generate secure one-time download token
      const tokenResponse = await generateFileTokenApi(cleanedLocation, true);

      if (!tokenResponse?.data?.success) {
        Toaster.error("Failed to generate secure download link");
        return;
      }

      const fileToken = tokenResponse.data.data.token;

      const backendUrl = process.env.REACT_APP_BACKEND_FILE_URL_ROOT;
      const encodedLocation = cleanedLocation
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");
      let fileUrl = backendUrl + encodedLocation;

      // Pass JWT + fileToken in URL (same as FolderDetails)
      const jwtToken = localStorage.getItem("accessToken");
      if (jwtToken) {
        const downloadUrl = fileUrl + `?token=${encodeURIComponent(jwtToken)}&fileToken=${encodeURIComponent(fileToken)}&download=1`;

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.setAttribute("download", "");
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        Toaster.success("Download started!");
      } else {
        Toaster.error("Authentication token not found. Please log in again.");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      Toaster.error("Failed to start download. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const openCreateFileModal = () => {
    const modalElement = document.getElementById("createFile");
    if (modalElement) {
      const modalInstance =
        Modal.getInstance(modalElement) || new Modal(modalElement);
      modalInstance.show(); // ✅ Force show modal
    }
  };
  // ✅ Handle File Selection
  const handleFileChange = (e) => {
    const file = e.target.files[0]; // Get the first selected file
    setSelectedFile(file);
  };
  // ✅ Handle File Upload
  const createFile = async () => {
    if (!selectedFile || !name.trim()) {
      Toaster.error("Please select a file and provide a name.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile); // Append selected file
      formData.append("name", name);

      const response = await callApi({
        endpoint: "/admin/default-file/upload",
        method: "POST",
        data: formData,
      });

      if (response?.success) {
        Toaster.success("File uploaded successfully!");

        fetchFoldersData(); // Reload files/folders
        setSelectedFile(null); // Reset file input
        setName(""); // Reset name input
        // ✅ Close modal properly
        const modalElement = document.getElementById("createFile");
        const modalInstance =
          Modal.getInstance(modalElement) || new Modal(modalElement);
        modalInstance.hide();

        document.body.classList.remove("modal-open"); // Remove backdrop effect
        document
          .querySelectorAll(".modal-backdrop")
          .forEach((backdrop) => backdrop.remove());
      } else {
        Toaster.error(response?.data?.message || "Failed to upload file.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      Toaster.error("Error uploading file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (file_id) => {
    const data = {
      file_id: file_id,
    };
    try {
      setLoading(true);
      await callApi({
        endpoint: `/admin/default-file/delete`,
        method: "DELETE",
        data: data,
      });
      // Remove from state
      Swal.fire("Deleted!", "File deleted Successfully.", "success");
      fetchFoldersData();
    } catch (error) {
      console.error("Error deleting file:", error);
      Swal.fire("Error", "Failed to delete file. Try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <ToastContainerComponent /> {/* ✅ Toaster Container for notifications */}
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="outer_holder_global_head">
                      <div className="global_head_tag">
                        <span>
                          <i className="fa-solid fa-folder-open"></i>
                        </span>{" "}
                        File Settings
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="action_block">
                      <a
                        className="add_folder"
                        href="#!"
                        onClick={openCreateFileModal}
                      >
                        <span>
                          <i className="fa-regular fa-file-pdf"></i>
                        </span>{" "}
                        Upload File
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="shared_folder_body Personal_table">
                <div className="shared_folder_body">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>File</th>
                        <th>Action</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {defaultFiles.length > 0 ? (
                        defaultFiles.map((item) => (
                          <tr key={item?.entity_id}>
                            <td>
                              <div className="floder_dtls">
                                <div className="floder_dtls_inner">
                                  <div
                                    className="folder-clickable floder_dtls_inner"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <p> {item?.name}</p>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="floder_dtls">
                                <div className="floder_dtls_inner">
                                  <div
                                    className="folder-clickable floder_dtls_inner"
                                    onClick={() =>
                                      detailsFolder(item?.location)
                                    }
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span>
                                      {item?.file_type !== "" ? (
                                        <i className="fa-regular fa-file-pdf"></i>
                                      ) : (
                                        <i className="fa-solid fa-folder-open"></i>
                                      )}
                                    </span>

                                    <p> {item?.filename}</p>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={item?.status === "active"} // or true/false based on your API data
                                  onChange={() => handleToggleStatus(item)}
                                />
                                <span className="slider round"></span>
                              </label>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-outline-secondary me-2"
                                onClick={() => detailsFolder(item?.location)}
                              >
                                <span>
                                  <i className="fa-solid fa-eye"></i>
                                </span>{" "}
                                Show
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => deleteFile(item?._id)}
                              >
                                <span>
                                  <i className="fa-solid fa-trash-can"></i>
                                </span>{" "}
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="3"
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No Files Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalFoldersData / filesPerPage)}
                  setCurrentPage={setCurrentPage}
                /> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade global_modal" tabIndex="-1" id="createFile">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create File</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="fileName">File</label>
                  <input
                    type="file"
                    id="fileName"
                    className="form-control"
                    placeholder="Choose File"
                    onChange={handleFileChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fileName">Name</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={name} // Bind value of the input to state
                    onChange={(e) => setName(e.target.value)} // Update the state when the input changes
                  />
                </div>
                <div className="form-group"></div>

                {/* ✅ Buttons */}
                <div className="btn_holder">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createFile}
                  >
                    Submit
                  </button>
                  <button
                    data-bs-dismiss="modal"
                    className="btn btn-primary orange_btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {previewModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <button
                className="close-button"
                onClick={() => setPreviewModal(false)}
              >
                ✖
              </button>
            </div>
            <iframe
              src={previewFileUrl}
              title="PDF Preview"
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default FileSettings;
